if (!customElements.get('blog-grid')) {
    class BlogGrid extends HTMLElement {
        #observer = null;

        #loading = false;

        #complete = false;

        #initPromise = null;

        #buffers = [];

        #shown = new Set();

        connectedCallback() {
            this.sectionId = this.getAttribute('section-id');
            this.mode = this.getAttribute('mode') || 'single';
            this.perPage = Number(this.getAttribute('page-size')) || 12;
            this.currentPage = Number(this.getAttribute('page-current')) || 1;
            this.pageTotal = Number(this.getAttribute('page-total')) || 1;

            this.elProducts = this.querySelector('[data-products]');
            this.elSentinel = this.querySelector('[data-sentinel]');
            this.elLoadMore = this.querySelector('[data-load-more]');
            this.elLoader = this.querySelector('[data-loader]');
            this.elStatus = this.querySelector('[data-status]');

            this.labelLoading = this.dataset.labelLoading || 'Loading more articles';
            this.labelLoaded = this.dataset.labelLoaded || '%count% more articles loaded';
            this.labelError = this.dataset.labelError || 'Unable to load more articles. Try again.';
            this.labelEnd = this.dataset.labelEnd || 'All articles loaded';

            if (!this.sectionId || !this.elProducts) {
                return;
            }

            if (this.mode === 'amalgamated') {
                this.#readConfig();
                this.#cleanBrowserUrl();
            }

            if (!this.#hasMore()) {
                this.#setComplete();
                return;
            }

            this.#initInfiniteScroll();
        }

        disconnectedCallback() {
            this.#observer?.disconnect();
        }

        #readConfig() {
            const elConfig = this.querySelector('[data-blog-grid-config]');
            const fallback = { blogs: [], shown: [], fragmentSectionId: 'main' };

            if (!elConfig) {
                this.config = fallback;
                return;
            }

            try {
                this.config = JSON.parse(elConfig.textContent);
            } catch (error) {
                console.error('blog-grid: invalid config JSON', error);
                this.config = fallback;
            }

            this.config.fragmentSectionId = this.config.fragmentSectionId || 'main';
            this.config.blogs = (this.config.blogs || [])
                .filter((blog) => blog?.handle?.trim())
                .map((blog) => ({
                    handle: blog.handle.trim(),
                    pages: Number(blog.pages) || 1,
                    fetchUrl: this.#blogFetchPath(blog),
                }))
                .filter((blog) => blog.fetchUrl);
            this.config.shown = this.config.shown || [];
            this.#shown = new Set(this.config.shown.map(String));
        }

        #blogFetchPath(blog) {
            const fetchUrl = blog.fetchUrl?.trim();

            if (fetchUrl && fetchUrl.includes('/blogs/')) {
                return fetchUrl.split('?')[0];
            }

            const handle = blog.handle?.trim();

            if (handle) {
                return `/blogs/${handle}`;
            }

            return null;
        }

        #cleanBrowserUrl() {
            const url = new URL(window.location.href);
            const preserve = new Set(['preview_theme_id']);
            let changed = false;

            for (const key of [...url.searchParams.keys()]) {
                if (!preserve.has(key)) {
                    url.searchParams.delete(key);
                    changed = true;
                }
            }

            if (!changed) {
                return;
            }

            const query = url.searchParams.toString();
            history.replaceState(null, '', query ? `${url.pathname}?${query}` : url.pathname);
        }

        #hasMore() {
            if (this.#complete) {
                return false;
            }

            if (this.mode === 'amalgamated') {
                return !this.#complete && this.config.blogs.length > 0 && this.pageTotal > 1;
            }

            return this.currentPage < this.pageTotal;
        }

        #initInfiniteScroll() {
            this.elLoadMore?.addEventListener('click', () => this.#loadMore());

            if (!this.elSentinel || !('IntersectionObserver' in window)) {
                return;
            }

            this.#observer = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            this.#loadMore();
                        }
                    }
                },
                { rootMargin: '600px 0px' }
            );
            this.#observer.observe(this.elSentinel);
        }

        #loadMore() {
            if (this.#loading || !this.#hasMore()) {
                return;
            }

            if (this.mode === 'amalgamated') {
                this.#loadMoreAmalgamated();
                return;
            }

            this.#loadMoreSingle();
        }

        #loadMoreSingle() {
            this.#setLoading(true);
            this.#announce(this.labelLoading);

            const nextPage = this.currentPage + 1;
            const url = new URL(window.location.href);
            url.searchParams.set('page', String(nextPage));
            url.searchParams.set('section_id', this.sectionId);

            this.#fetchCards(url.toString(), this.sectionId)
                .then((cards) => {
                    this.#appendCards(cards);
                    this.currentPage = nextPage;
                    this.setAttribute('page-current', String(nextPage));
                    this.#afterLoad(cards.length);
                })
                .catch((error) => this.#handleError(error));
        }

        #loadMoreAmalgamated() {
            this.#setLoading(true);
            this.#announce(this.labelLoading);

            this.#ensureInit()
                .then(() => this.#emitBatch(this.perPage))
                .then((cards) => {
                    this.#appendCards(cards);
                    this.#afterLoad(cards.length);
                })
                .catch((error) => this.#handleError(error));
        }

        #ensureInit() {
            if (this.#initPromise) {
                return this.#initPromise;
            }

            this.#initPromise = Promise.all(
                this.config.blogs.map((blog) =>
                    this.#fetchBlogPage(blog, 1).then((cards) => {
                        this.#buffers.push({
                            handle: blog.handle,
                            fetchUrl: blog.fetchUrl,
                            cards,
                            nextPage: 2,
                            pages: blog.pages,
                            exhausted: false,
                        });
                    })
                )
            );

            return this.#initPromise;
        }

        async #emitBatch(count) {
            const results = [];

            while (results.length < count) {
                await this.#refillEmptyBuffers();

                const active = this.#buffers.filter((buffer) => buffer.cards.length > 0);

                if (active.length === 0) {
                    this.#complete = true;
                    break;
                }

                let newest = active[0];

                for (const buffer of active) {
                    if (Number(buffer.cards[0].dataset.publishedAt) > Number(newest.cards[0].dataset.publishedAt)) {
                        newest = buffer;
                    }
                }

                results.push(newest.cards.shift());
            }

            return results;
        }

        #refillEmptyBuffers() {
            const pending = this.#buffers
                .filter((buffer) => !buffer.exhausted && buffer.cards.length === 0)
                .map((buffer) => this.#fillBuffer(buffer));

            return Promise.all(pending);
        }

        async #fillBuffer(buffer) {
            while (buffer.cards.length === 0 && !buffer.exhausted) {
                if (buffer.nextPage > buffer.pages) {
                    buffer.exhausted = true;
                    return;
                }

                const cards = await this.#fetchBlogPage(buffer, buffer.nextPage);
                buffer.nextPage += 1;

                if (cards.length > 0) {
                    buffer.cards.push(...cards);
                }
            }
        }

        #fetchBlogPage(blog, page) {
            const fetchPath = blog.fetchUrl || this.#blogFetchPath(blog);

            if (!fetchPath?.includes('/blogs/')) {
                return Promise.resolve([]);
            }

            const url = new URL(fetchPath, window.location.origin);

            if (!url.pathname.startsWith('/blogs/')) {
                return Promise.resolve([]);
            }

            url.searchParams.set('page', String(page));

            const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');

            if (previewThemeId) {
                url.searchParams.set('preview_theme_id', previewThemeId);
            }

            return this.#fetchCards(url.toString()).then((cards) =>
                cards.filter((card) => !this.#shown.has(card.dataset.articleId))
            );
        }

        #fetchCards(url, sectionId = null) {
            return fetch(url)
                .then((response) => {
                    if (response.status === 404) {
                        return '';
                    }

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    return response.text();
                })
                .then((text) => {
                    if (!text) {
                        return [];
                    }

                    const doc = new DOMParser().parseFromString(text, 'text/html');
                    let container = null;

                    if (sectionId) {
                        const section = doc.querySelector(`#shopify-section-${sectionId}`) || doc;
                        container = section.querySelector('[data-products]');
                    }

                    if (!container) {
                        container =
                            doc.querySelector('blog-grid [data-products]') || doc.querySelector('[data-products]');
                    }

                    if (!container) {
                        throw new Error('Unexpected response: blog-grid section not found');
                    }

                    return [...container.querySelectorAll('.article-card')];
                });
        }

        #appendCards(cards) {
            if (!cards.length) {
                return;
            }

            const fragment = document.createDocumentFragment();

            for (const card of cards) {
                fragment.appendChild(card);
            }

            this.elProducts.appendChild(fragment);
            window.initLazyLoad?.();
        }

        #afterLoad(count) {
            this.#setLoading(false);
            this.#announce(this.labelLoaded.replace('%count%', String(count)));

            if (!this.#hasMore()) {
                this.#setComplete();
            }
        }

        #handleError(error) {
            this.#setLoading(false);
            this.#announce(this.labelError);
            console.error('blog-grid load failed', error);
        }

        #setLoading(isLoading) {
            this.#loading = isLoading;
            this.classList.toggle('loading', isLoading);

            if (this.elLoader) {
                this.elLoader.hidden = !isLoading;
                this.elLoader.setAttribute('aria-hidden', String(!isLoading));
            }

            if (this.elLoadMore) {
                this.elLoadMore.disabled = isLoading;
            }
        }

        #setComplete() {
            this.#complete = true;
            this.#observer?.disconnect();

            if (this.elSentinel) {
                this.elSentinel.hidden = true;
            }

            if (this.elLoadMore) {
                this.elLoadMore.hidden = true;
            }

            if (this.elLoader) {
                this.elLoader.hidden = true;
                this.elLoader.setAttribute('aria-hidden', 'true');
            }

            this.#announce(this.labelEnd);
        }

        #announce(message) {
            if (this.elStatus) {
                this.elStatus.textContent = message;
            }
        }
    }

    customElements.define('blog-grid', BlogGrid);
}
